import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  model,
  output,
  signal,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { OrderDetailDto } from '../../core/models/order.models';
import { ReturnService } from '../../core/services/return.service';
import { CreateReturnItemRequest, ReturnItemCondition } from '../../core/models/return.models';
import { MoneyPipe } from '../../shared/pipes/money.pipe';
import { CurrencyService } from '../../core/services/currency.service';

/** Bazadagi miqdor aniqligi — `OrderReturnItem.Quantity` = `decimal(18,6)`. */
const QUANTITY_DECIMALS = 6;

interface ReturnRow {
  productId: string;
  productVariantId: string;
  productName: string;
  variantLabel: string | null;
  purchased: number;
  returned: number;
  available: number;
  unitRefund: number; // unitPrice - discount
  selected: boolean;
  qty: number;
  condition: ReturnItemCondition;
  restock: boolean;
  refundAmount: number;
  refundTouched: boolean;
}

@Component({
  selector: 'app-return-dialog',
  standalone: true,
  imports: [
    MoneyPipe,
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    CheckboxModule,
    InputNumberModule,
    SelectModule,
    InputTextModule,
    TooltipModule,
    TranslatePipe,
  ],
  templateUrl: './return-dialog.component.html',
})
export class ReturnDialogComponent {
  private returnSvc = inject(ReturnService);
  private translate = inject(TranslateService);
  private readonly currencySvc = inject(CurrencyService);
  private readonly destroyRef = inject(DestroyRef);

  readonly Condition = ReturnItemCondition;

  // Two-way bound visibility
  visible = model<boolean>(false);
  // Order to return from
  order = input<OrderDetailDto | null>(null);
  // Emitted after a successful return so the parent can refresh
  returned = output<void>();

  rows = signal<ReturnRow[]>([]);
  reason = '';
  submitting = signal(false);

  conditionOptions = computed(() => [
    {
      label: this.translate.instant('ORDERS.RETURN.CONDITIONS.SELLABLE'),
      value: ReturnItemCondition.Sellable,
    },
    {
      label: this.translate.instant('ORDERS.RETURN.CONDITIONS.DEFECTIVE'),
      value: ReturnItemCondition.Defective,
    },
    {
      label: this.translate.instant('ORDERS.RETURN.CONDITIONS.OPENED'),
      value: ReturnItemCondition.Opened,
    },
    {
      label: this.translate.instant('ORDERS.RETURN.CONDITIONS.EXPIRED'),
      value: ReturnItemCondition.Expired,
    },
  ]);

  isDebtOrder = computed(() => this.order()?.paymentType === 'Debt');

  /** Buyurtma valyutasi — barcha summalar shunda ko'rsatiladi (I9). */
  orderCurrency = computed(() => this.order()?.currency ?? '');

  orderTotal = computed(() => this.order()?.totalAmount ?? 0);
  orderPaid = computed(() => this.order()?.paidAmount ?? 0);
  orderDebt = computed(() => {
    const total = this.orderTotal();
    const paid = this.orderPaid();
    return Math.max(0, total - paid);
  });

  availableTotal = computed(() =>
    this.rows().reduce((sum, r) => sum + r.unitRefund * r.available, 0),
  );

  originalTotalRefund = computed(() =>
    this.rows()
      .filter((r) => r.selected)
      .reduce((sum, r) => sum + r.unitRefund * r.qty, 0),
  );

  totalRefund = computed(() =>
    this.rows()
      .filter((r) => r.selected)
      .reduce((sum, r) => sum + (r.refundAmount || 0), 0),
  );

  unreturnedTotal = computed(() => Math.max(0, this.availableTotal() - this.totalRefund()));

  itemWithheld = computed(() => Math.max(0, this.originalTotalRefund() - this.totalRefund()));

  enteredCashRefund = signal<number | null>(null);

  paidRefundTotal = computed(() => {
    const totalRef = this.totalRefund();
    if (!this.isDebtOrder()) return totalRef;
    const paid = this.orderPaid();
    const unreturned = this.unreturnedTotal();
    return Math.min(totalRef, Math.max(0, paid - unreturned));
  });

  defaultDebtReduction = computed(() => {
    if (!this.isDebtOrder()) return 0;
    return Math.max(0, this.totalRefund() - this.paidRefundTotal());
  });

  defaultCashRefund = computed(() => {
    if (!this.isDebtOrder()) return this.totalRefund();
    return this.paidRefundTotal();
  });

  debtReductionValue = computed(() => this.defaultDebtReduction());

  cashRefundValue = computed(() => {
    const entered = this.enteredCashRefund();
    if (entered !== null) return entered;
    return this.defaultCashRefund();
  });

  get cashRefundModel(): number {
    return this.cashRefundValue();
  }
  set cashRefundModel(val: number) {
    const maxVal = this.defaultCashRefund();
    let cleanVal = val;
    if (cleanVal < 0) cleanVal = 0;
    if (cleanVal > maxVal) cleanVal = maxVal;
    this.enteredCashRefund.set(cleanVal);
  }

  cashWithheld = computed(() => Math.max(0, this.defaultCashRefund() - this.cashRefundValue()));

  withheldAmount = computed(() => this.itemWithheld() + this.cashWithheld());

  hasReturnable = computed(() => this.rows().length > 0);

  constructor() {
    // Rebuild editable rows whenever a new order is opened.
    effect(() => {
      const order = this.order();
      this.reason = '';
      if (!order) {
        this.rows.set([]);
        return;
      }
      const rows: ReturnRow[] = order.items
        .map((i) => {
          // Ayirma miqdor aniqligiga keltiriladi: `1 - 0.999` suzuvchi nuqtada
          // `0.0009999999999999432` beradi va u qaytarish miqdori sifatida
          // ekranga ham, so'rovga ham o'sha holda tushardi.
          const available = Number(
            (i.quantity - (i.returnedQuantity ?? 0)).toFixed(QUANTITY_DECIMALS),
          );
          const unitRefund = i.unitPrice - i.discount;
          return {
            productId: i.productId,
            productVariantId: i.productVariantId,
            productName: i.productName,
            variantLabel: i.variantLabel ?? null,
            purchased: i.quantity,
            returned: i.returnedQuantity ?? 0,
            available,
            unitRefund,
            selected: false,
            qty: available,
            condition: ReturnItemCondition.Sellable,
            restock: true,
            refundAmount: this.currencySvc.roundPrice(unitRefund * available, order.currency),
            refundTouched: false,
          } as ReturnRow;
        })
        .filter((r) => r.available > 0);
      this.rows.set(rows);
      this.enteredCashRefund.set(null);
    });
  }

  onRowSelectToggle(row: ReturnRow) {
    if (row.selected) {
      if (row.qty <= 0) {
        row.qty = row.available;
      }
      row.refundAmount = row.unitRefund * row.qty;
      row.refundTouched = false;
    }
    this.enteredCashRefund.set(null);
    this.rows.set([...this.rows()]);
  }

  onQtyChange(row: ReturnRow) {
    if (row.qty > row.available) row.qty = row.available;
    if (row.qty < 0) row.qty = 0;
    // Qaytariladigan summa — mijozga beriladigan pul, shuning uchun ko'paytma
    // valyuta aniqligiga keltiriladi (`0.999 × 4.4234` dumini qoldirmasin).
    if (!row.refundTouched)
      row.refundAmount = this.currencySvc.roundPrice(
        row.unitRefund * row.qty,
        this.orderCurrency(),
      );
    this.enteredCashRefund.set(null);
    this.rows.set([...this.rows()]);
  }

  onConditionChange(row: ReturnRow) {
    // Sukut bo'yicha: yaroqli → sotuvga qaytadi, qolganlari → brak omboriga.
    row.restock = row.condition === ReturnItemCondition.Sellable;
    this.rows.set([...this.rows()]);
  }

  onRefundChange(row: ReturnRow) {
    row.refundTouched = true;
    if (row.refundAmount < 0) row.refundAmount = 0;
    this.rows.set([...this.rows()]);
  }

  close() {
    this.visible.set(false);
  }

  submit() {
    const selected = this.rows().filter((r) => r.selected && r.qty > 0);
    const order = this.order();
    if (!order) return;
    if (selected.length === 0) {
      this.returnSvc.showWarn(this.translate.instant('ORDERS.RETURN.SELECT_AT_LEAST_ONE'));
      return;
    }

    const items: CreateReturnItemRequest[] = selected.map((r) => ({
      productId: r.productId,
      quantity: r.qty,
      productVariantId: r.productVariantId || null,
      condition: r.condition,
      restockToSellable: r.restock,
      refundAmount: r.refundAmount,
    }));

    const withheldAmount = this.withheldAmount();

    this.submitting.set(true);
    this.returnSvc
      .create({ orderId: order.id, items, reason: this.reason || 'Return', withheldAmount })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.returnSvc.showSuccess(this.translate.instant('ORDERS.RETURN.SUCCESS'));
          this.submitting.set(false);
          this.visible.set(false);
          this.returned.emit();
        },
        error: () => {
          // Xato toasti api-error.interceptor tomonidan avtomatik ko'rsatiladi.
          this.submitting.set(false);
        },
      });
  }
}
